/**
 * 轻量 Toast。
 * 用 context + portal，不引第三方库；错误统一用 rose 色，成功用 emerald 色，
 * 保证"同一个语义永远是同一个颜色"。
 *
 * ## 动作按钮（2026-10-17 新增）
 *
 * 起因是用户报的那个误触：孩子想点「完成」，**不小心点了放弃**。
 * 放弃成功之后必须**立刻**给一个看得见的回程，而"回到那一行去找按钮"
 * 对小孩来说太远了 —— 于是 toast 需要能带一个「撤销」按钮。
 *
 * 为什么把它做进 toast 而不是另造一个"撤销条"组件：
 *  - 已有的 toast 就是全站唯一的"这件事刚刚发生了"通道，位置（底部、手机上
 *    `bottom-20` 避开底部 tab）、层级、动画都已经调好；
 *  - 再造一个浮层要重新解决同一批问题（被 tab 挡住、与 toast 叠在一起）。
 *
 * ⚠️ 三条刻意的约束：
 *  1. `action` 是**可选**的，`showToast(msg, tone)` 的老调用**一个字都不用改**；
 *  2. 带动作的 toast **必须**给更长的停留时间（调用方传 `durationMs`，
 *     默认仍是 3 秒）—— 3 秒内要读完一句话再点到按钮，对孩子不现实；
 *  3. 动作按钮**不**改变任何业务语义：它只是替用户点一下那个本来就在行内存在的按钮
 *     （「恢复成未完成」），所以点了之后照样走同一套 mutation 与失效逻辑。
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export type ToastTone = 'success' | 'error' | 'info';

/** toast 上的一个动作按钮（例如「撤销」）。 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** 动作按钮。给了就渲染在文案右边（触摸区 ≥44px，见下方按钮样式）。 */
  action?: ToastAction;
  /** 停留时长（毫秒）。默认 3000；带动作时调用方应当传更长的一个值。 */
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
  durationMs: number;
}

interface ToastContextValue {
  showToast: (message: string, tone?: ToastTone, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONES: Record<ToastTone, string> = {
  success: 'bg-emerald-600 text-white',
  error: 'bg-rose-600 text-white',
  info: 'bg-slate-900 text-white',
};

/** 默认停留 3 秒：家长在手机上操作时不必手动关闭。 */
const DEFAULT_DURATION_MS = 3_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, tone: ToastTone = 'info', options?: ToastOptions) => {
      const id = nextId.current++;
      const durationMs = Math.max(1_000, options?.durationMs ?? DEFAULT_DURATION_MS);
      const item: ToastItem = {
        id,
        message,
        tone,
        durationMs,
        // 显式只在有值时才带这个键（`exactOptionalPropertyTypes` 下 undefined 不能赋值）
        ...(options?.action ? { action: options.action } : {}),
      };
      setItems((prev) => [...prev, item]);
      globalThis.setTimeout(() => dismiss(id), durationMs);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {items.length > 0 &&
        createPortal(
          <div
            className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6"
            role="status"
            aria-live="polite"
          >
            {items.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'animate-toast-in pointer-events-auto flex max-w-md items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium shadow-lg',
                  TONES[item.tone],
                )}
              >
                <span className="min-w-0 flex-1">{item.message}</span>
                {item.action && (
                  /*
                   * 动作按钮：白底/半透明底 + 深色字，与 toast 的深色背景形成对比。
                   * `min-h-[44px]` 是触摸区下限（与 `Button` 的 `md` 同高），
                   * `whitespace-nowrap` 保证「撤销」两个字不会被挤成两行。
                   */
                  <button
                    type="button"
                    onClick={() => {
                      /*
                       * 先收起这条 toast 再执行动作：
                       * 动作是"恢复成未完成"，成功后会再弹一条结果 toast ——
                       * 不收掉的话两条会同时挂在底部，看起来像点了两次。
                       */
                      dismiss(item.id);
                      item.action?.onClick();
                    }}
                    className="min-h-[44px] shrink-0 whitespace-nowrap rounded-lg bg-white/95 px-3 py-1.5 text-sm font-bold text-slate-900 hover:bg-white active:bg-slate-100"
                  >
                    {item.action.label}
                  </button>
                )}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast 必须在 <ToastProvider> 内使用');
  return ctx;
}
